import React, { useEffect, useState } from 'react';

import Grid from '@mui/material/Grid2';

import { 
  MinionAbility,
  UpgradableNumber,
  ConsumedResource,
  AttachedTable,
  AbilityEffect
} from "@dndModels";
import { 
  ABILITY_SCORES_EXPANDED, 
  DURATIONS,
  COMPONENTS,
  // REFRESH_RULES,
  STANDARD_CASTING_TIMES
} from "@dndModels/Constants";
import { CastingTime, DurationType } from "@dndModels/Types";

import StringBox from "@components/input/strings/StringBox";
import SelectStringBox from "@components/input/selects/SelectStringBox";
import CheckBox from "@components/input/CheckBox";
import UpgradableNumberBox from "@dndComponents/UpgradableNumberBox";

import ConsumedResourceInput from "@dndComponents/feature/ConsumedResourceInput";
import EffectsInput from "@dndComponents/feature/EffectsInput";

interface Props {
  obj: MinionAbility;
  slot_level?: number;
  onChange: (changed: MinionAbility) => void; 
  disabled?: boolean;
  attached_table?: AttachedTable;
}

export default function MinionAbilityInput({
  obj,
  slot_level = -1,
  disabled = false,
  attached_table,
  onChange,
}: Props) {
  const [ping, setPing] = useState(false);
  const [reloading, setReloading] = useState(false);

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);
  
  return (
    <Grid container spacing={0} direction="row">
      <Grid
        size={{
          xs: 2
        }}>
        <SelectStringBox 
          name="Casting Time"
          options={STANDARD_CASTING_TIMES}
          extra_options={["Special"]}
          value={obj.casting_time}
          disabled={disabled}
          onChange={(changed: CastingTime) => {
            const new_obj = obj;
            new_obj.casting_time = changed;
            onChange(new_obj);
          }}
        /> 
      </Grid>
      <Grid
        size={{
          xs: 2
        }}>
        <UpgradableNumberBox 
          name="Attack Bonus"
          slot_level={slot_level}
          value={obj.attack_bonus} 
          disabled={disabled}
          attached_table={attached_table}
          onChange={(value: UpgradableNumber) => {
            const new_obj = obj;
            new_obj.attack_bonus = value;
            onChange(new_obj);
          }}
        />
      </Grid>
      <Grid
        container
        spacing={0}
        direction="row"
        size={{
          xs: 4
        }}>
        <Grid
          size={{
            xs: 6
          }}>
          <StringBox 
            value={obj.range} 
            name="Range"
            disabled={disabled}
            onBlur={(value: string) => {
              const new_obj = obj;
              new_obj.range = value;
              onChange(new_obj);
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 6
          }}>
          <StringBox 
            value={obj.range_2} 
            name="Area"
            disabled={disabled}
            onBlur={(value: string) => {
              const new_obj = obj;
              new_obj.range_2 = value;
              onChange(new_obj);
            }}
          />
        </Grid>
      </Grid>
      <Grid
        size={{
          xs: 4
        }}>
        <SelectStringBox 
          name="Components"
          options={COMPONENTS}
          values={obj.components}
          multiple
          disabled={disabled}
          onChange={(values: string[]) => {
            const new_obj = obj;
            new_obj.components = values;
            onChange(new_obj);
          }}
        /> 
      </Grid>
      { obj.casting_time === "Special" && 
        <Grid
          size={{
            xs: 12
          }}>
          <StringBox 
            name="Special Casting Time"
            value={obj.special_casting_time}
            disabled={disabled}
            onBlur={(changed: string) => {
              const new_obj = obj;
              new_obj.special_casting_time = changed;
              onChange(new_obj);
            }}
          /> 
        </Grid>
      }
      <Grid
        container
        spacing={0}
        direction="row"
        size={{
          xs: 4
        }}>
        <Grid
          size={{
            xs: obj.duration === "Instantaneous" ? 12 : 9
          }}>
          <SelectStringBox 
            name="Duration"
            options={DURATIONS}
            disabled={disabled}
            value={ !DURATIONS.includes(obj.duration) ? "Special" : obj.duration }
            onChange={(value: DurationType) => {
              const new_obj = obj;
              new_obj.duration = value;
              onChange(new_obj);
            }}
          /> 
        </Grid>
        { obj.duration !== "Instantaneous" && 
          <Grid
            size={{
              xs: 3
            }}>
            <CheckBox 
              name="C" 
              value={obj.concentration} 
              disabled={disabled}
              onChange={(e: boolean) => {
                const new_obj = obj;
                new_obj.concentration = e;
                onChange(new_obj);
              }} 
            />
          </Grid>
        } 
        { (obj.duration === "Special" || !DURATIONS.includes(obj.duration)) && 
          <Grid
            size={{
              xs: 12
            }}>
            <StringBox 
              name="Duration"
              disabled={disabled}
              value={obj.duration === "Special" ? "" : obj.duration}
              onBlur={(value: DurationType) => {
                const new_obj = obj;
                new_obj.duration = value;
                onChange(new_obj);
              }}
            /> 
          </Grid>
        } 
      </Grid>
      <Grid
        size={{
          xs: 2
        }}>
        <SelectStringBox 
          name="Saving Throw"
          options={ABILITY_SCORES_EXPANDED}
          value={obj.saving_throw_ability_score}
          disabled={disabled}
          onChange={(value: string) => {
            const new_obj = obj;
            new_obj.saving_throw_ability_score = value;
            onChange(new_obj);
          }}
        /> 
      </Grid>
      <Grid
        size={{
          xs: 2
        }}>
        <UpgradableNumberBox 
          name="DC"
          slot_level={slot_level}
          value={obj.dc} 
          disabled={disabled}
          attached_table={attached_table}
          onChange={(value: UpgradableNumber) => {
            const new_obj = obj;
            new_obj.dc = value;
            onChange(new_obj);
          }}
        />
      </Grid>
      { obj.components.includes("M") &&
        <Grid
          size={{
            xs: 12
          }}>
          <StringBox 
            name="Material Component"
            value={obj.material_component}
            disabled={disabled}
            onBlur={(value: string) => {
              const new_obj = obj;
              new_obj.material_component = value;
              onChange(new_obj);
            }}
          /> 
        </Grid>
      }
      <Grid
        size={{
          xs: 12
        }}>
        <EffectsInput
          effects={obj.effects}
          disabled={disabled}
          for_obj={obj}
          attached_table={attached_table}
          onChange={(changed: AbilityEffect[]) => {
            const new_obj = obj;
            obj.effects = changed;
            onChange(new_obj);
            setPing(!ping);
          }}
        />
      </Grid>
      <Grid
        size={{
          xs: 12
        }}>
        <ConsumedResourceInput 
          obj={obj.consumed_resource} 
          disabled={false}
          onChange={(changed: ConsumedResource) => {
            const new_obj = obj;
            new_obj.consumed_resource = changed;
            onChange(new_obj);
            // setReloading(true);
          }}
        />
      </Grid>
    </Grid>
  );
}
